import type { CompanySettings, DayInput, Employee, PeriodInfo } from './types.js';

/** Local-safe ISO date — `toISOString()` would shift the day in negative offsets. */
export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseDate(ds: string): Date {
  return new Date(`${ds}T12:00:00`);
}

/** Today as an ISO date in the runtime's local zone. */
export function todayIso(): string {
  return iso(new Date());
}

/**
 * The pay period containing `todayStr`, shifted by `offset` periods.
 * `offset: -1` is the previous period, `0` the current one.
 */
export function periodInfo(settings: CompanySettings, todayStr: string, offset = 0): PeriodInfo {
  const today = parseDate(todayStr);
  const y = today.getFullYear();
  const m = today.getMonth();
  let start: Date;
  let end: Date;

  if (settings.periodType === 'week') {
    const dow = (today.getDay() + 6) % 7; // Monday = 0
    start = new Date(today);
    start.setDate(today.getDate() - dow + 7 * offset);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (settings.periodType === 'half') {
    const idx = m * 2 + (today.getDate() <= 15 ? 0 : 1) + offset;
    const mm = Math.floor(idx / 2);
    const half = ((idx % 2) + 2) % 2;
    if (half === 0) {
      start = new Date(y, mm, 1);
      end = new Date(y, mm, 15);
    } else {
      start = new Date(y, mm, 16);
      end = new Date(y, mm + 1, 0);
    }
  } else {
    start = new Date(y, m + offset, 1);
    end = new Date(y, m + offset + 1, 0);
  }

  const days: PeriodInfo['days'] = [];
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const ds = iso(d);
    const dow = d.getDay();
    days.push({
      date: ds,
      dow,
      weekend: dow === 0 || dow === 6,
      holiday: settings.holidays.includes(ds),
      future: ds > todayStr,
      isToday: ds === todayStr,
    });
  }
  return { start: iso(start), end: iso(end), days };
}

/**
 * The employee's worklog merged with the running clock, for one period.
 * Today counts live clock hours *plus* anything logged or corrected for today.
 */
export function daysFor(
  emp: Pick<Employee, 'worklog'>,
  settings: CompanySettings,
  todayStr: string,
  liveHoursToday = 0,
  offset = 0,
): DayInput[] {
  const p = periodInfo(settings, todayStr, offset);
  return p.days
    .filter((d) => !d.future)
    .map((d) => {
      const w = emp.worklog[d.date] || {};
      const h = d.isToday ? (liveHoursToday || 0) + (w.h || 0) : w.h || 0;
      return { ...d, h, sick: !!w.sick, vacation: !!w.vacation, periodDays: p.days.length };
    });
}

/** An arbitrary date range as engine input — used for YTD and monthly matrices. */
export function daysForRange(
  emp: Pick<Employee, 'worklog'>,
  settings: CompanySettings,
  from: string,
  to: string,
  periodDaysOverride?: number,
): DayInput[] {
  const days: DayInput[] = [];
  for (const d = parseDate(from); iso(d) <= to; d.setDate(d.getDate() + 1)) {
    const ds = iso(d);
    const dow = d.getDay();
    const w = emp.worklog[ds] || {};
    days.push({
      date: ds,
      dow,
      weekend: dow === 0 || dow === 6,
      holiday: settings.holidays.includes(ds),
      h: w.h || 0,
      sick: !!w.sick,
      vacation: !!w.vacation,
      periodDays: 0,
    });
  }
  const n = periodDaysOverride || days.length || 1;
  days.forEach((x) => {
    x.periodDays = n;
  });
  return days;
}

/** Contracted work time for a period: calendar workdays × the daily threshold. */
export function expectedWork(
  pinfo: PeriodInfo,
  settings: CompanySettings,
  overrideHours = 0,
): { days: number; hours: number } {
  const days = pinfo.days.filter((d) => !d.weekend && !d.holiday).length;
  return { days, hours: overrideHours > 0 ? overrideHours : days * settings.dailyThreshold };
}

/** Payday — the day after the period closes. */
export function payoutDate(pinfo: PeriodInfo): string {
  const d = parseDate(pinfo.end);
  d.setDate(d.getDate() + 1);
  return iso(d);
}

export function periodKey(pinfo: PeriodInfo): string {
  return `${pinfo.start}_${pinfo.end}`;
}
